import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import { ICONS, PROMOTION_PACKETS } from 'consts/consts';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { CalibrationInfo, TeamDashboardPerson } from 'types';
import { formatChangeDelta } from 'utils/custom-question/formatter';
import { PACKET_STATUSES } from 'utils/models/PromotionPackets';
import { usePromotionNominationConfig } from 'views/Performance/PerformancePersonAssessment';
import { toBoolean } from 'views/Widgets/Inputs/MultipleChoiceInput';
import ScrollableUncontrolledPopover from 'views/Widgets/ScrollableUncontrolledPopover';
import { useCalibration } from './Calibration/CalibrationContext';
import CalibrationPopover from './Calibration/CalibrationPopover';
import TeamDashboardCalibrationEditor from './Calibration/TeamDashboardCalibrationEditor';
import {
  extractCalibrationInfoForNominatedForPromotion,
  RECOMMEND_FOR_PROMOTION_QUESTION,
} from './Calibration/utils';

interface Props {
  uniqueId: string;
  person: TeamDashboardPerson;
  promotionPacketsRequired: boolean;
  showPromo: boolean;
  setCurrentPromotionPacketId: (id?: any) => void;
}

const TeamDashboardManagerPromotion: FC<Props> = ({
  uniqueId,
  person,
  promotionPacketsRequired,
  showPromo,
  setCurrentPromotionPacketId,
}) => {
  const { formatMessage } = useIntl();

  const {
    isCalibrating,
    surveyResponseIdToCalibrationItems,
    campaign,
    calibratePromotionNominationsEnabled,
    calibrationViewId,
  } = useCalibration();

  const calibrationInfo: CalibrationInfo = useMemo(
    () =>
      extractCalibrationInfoForNominatedForPromotion({
        manager: person.manager,
        recommend_for_promotion: person.extracted_recommend_for_promotion,
        promotion_comments: person.promotion_comments,
        // @ts-expect-error
        surveyResponseId: person.survey_response_id,
        surveyResponseIdToCalibrationItems,
        recommend_for_promotion_additional_managers:
          person.recommend_for_promotion_additional_managers,
      }),
    [
      person.manager,
      person.extracted_recommend_for_promotion,
      person.recommend_for_promotion_additional_managers,
      person.promotion_comments,
      // @ts-expect-error
      person.survey_response_id,
      surveyResponseIdToCalibrationItems,
    ]
  );

  const answeredQuestionInCycle =
    person.extracted_recommend_for_promotion != null;
  const hasActivePromotionPacket = !!person.packet;
  const personRecommendedForPromotion =
    toBoolean(calibrationInfo.currentValue) || hasActivePromotionPacket;
  const canEdit = !!person?.can_edit_final_rating;

  const { promotionQuestionLabel, promotionHelperText } =
    usePromotionNominationConfig({
      campaign,
      person,
    });

  const question = useMemo(
    () =>
      RECOMMEND_FOR_PROMOTION_QUESTION(
        promotionQuestionLabel,
        promotionHelperText
      ),
    [promotionQuestionLabel, promotionHelperText]
  );
  const uniquePopoverId = 'row-role-recommend-for-promo-' + uniqueId;

  return (
    <>
      {/* Version with promo packets */}
      {promotionPacketsRequired && (
        <>
          <div className="d-flex align-items-center">
            <div id={uniquePopoverId} className="d-flex align-items-center">
              {isCalibrating &&
                answeredQuestionInCycle &&
                calibratePromotionNominationsEnabled &&
                calibrationInfo &&
                canEdit && (
                  <TeamDashboardCalibrationEditor
                    question={question}
                    // @ts-expect-error
                    targetPerson={person}
                    calibrationInfo={calibrationInfo}
                    editable={true}
                  />
                )}
              {personRecommendedForPromotion && (
                <span>
                  <i className={ICONS.PROMOTION} />
                </span>
              )}
              {calibrationInfo && (
                <span className="ms-1 text-muted">
                  {formatChangeDelta(question, calibrationInfo)}
                </span>
              )}
            </div>
            <span
              id={uniquePopoverId + '-packet-icon'}
              role={person?.packet?.id ? 'button' : undefined}
              onClick={
                person?.packet?.id
                  ? () => setCurrentPromotionPacketId(person?.packet?.id)
                  : undefined
              }
            >
              {person.packet ? (
                <span className="ms-1">
                  <i
                    className={
                      // @ts-expect-error
                      PACKET_STATUSES.find(
                        (s) => s.id === person.packet?.status
                      ).icon
                    }
                  />
                </span>
              ) : (
                <span>
                  {personRecommendedForPromotion && (
                    <i className="fe fe-help-circle text-muted" />
                  )}
                </span>
              )}
            </span>
          </div>
          <SafeUncontrolledPopover
            placement="left"
            trigger="hover"
            target={uniquePopoverId + '-packet-icon'}
          >
            {person.packet && (
              <>
                <i
                  className={
                    'pe-2 ' +
                    // @ts-expect-error
                    PACKET_STATUSES.find((s) => s.id === person.packet?.status)
                      .icon
                  }
                />
                {
                  // @ts-expect-error
                  PACKET_STATUSES.find((s) => s.id === person.packet?.status)
                    .description
                }
                <br />
                <br />
                <Link
                  to={
                    PROMOTION_PACKETS(formatMessage).path +
                    '/' +
                    person.packet.id +
                    (calibrationViewId
                      ? '?calibrationViewId=' + calibrationViewId
                      : '')
                  }
                  target={'_blank'}
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="app.views.widgets.dashboards.team_dashboard.open_packet_in_new_window"
                    defaultMessage="Open packet in new window"
                  />{' '}
                  <i className="fe fe-external-link" />
                </Link>
              </>
            )}
            {!person.packet && (
              <>
                <i className="fe fe-help-circle text-muted pe-2" />
                <FormattedMessage
                  id="app.views.widgets.dashboards.team_dashboard.no_promo_packet_created_yet"
                  defaultMessage="No promotion packet has been created yet."
                />
                <br />
                <br />
                <Link
                  to={PROMOTION_PACKETS(formatMessage).path}
                  target={'_blank'}
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="app.views.widgets.dashboards.team_dashboard.create_promo_packet_action"
                    defaultMessage="Create promotion packet"
                  />{' '}
                  <i className="fe fe-external-link" />
                </Link>
              </>
            )}
          </SafeUncontrolledPopover>
          <CommentsPopover
            person={person}
            uniquePopoverId={uniquePopoverId}
            calibrationInfo={calibrationInfo}
            question={question}
          />
        </>
      )}
      {/* Version without promo packets */}
      {!promotionPacketsRequired && showPromo && (
        <>
          <div id={uniquePopoverId} className="d-flex align-items-center">
            {/* Case when calibrating promotion */}
            {isCalibrating &&
              answeredQuestionInCycle &&
              calibratePromotionNominationsEnabled &&
              calibrationInfo &&
              canEdit && (
                <TeamDashboardCalibrationEditor
                  question={question}
                  // @ts-expect-error
                  targetPerson={person}
                  calibrationInfo={calibrationInfo}
                  editable={true}
                />
              )}
            {/* Case when not calibrating  */}
            {personRecommendedForPromotion && (
              <>
                <span>
                  <i className={ICONS.PROMOTION} />
                </span>
              </>
            )}
            {calibrationInfo && (
              <span className="ms-1 text-muted">
                {formatChangeDelta(question, calibrationInfo)}
              </span>
            )}
          </div>
          <CommentsPopover
            person={person}
            uniquePopoverId={uniquePopoverId}
            calibrationInfo={calibrationInfo}
            question={question}
          />
        </>
      )}
    </>
  );
};

const CommentsPopover = ({
  person,
  uniquePopoverId,
  calibrationInfo,
  question,
}) => {
  return (
    <>
      {(person.extracted_recommend_for_promotion != undefined ||
        !!person.promotion_comments?.length ||
        !!person?.recommend_for_promotion_additional_managers?.length ||
        !!calibrationInfo?.calibrationItems?.length) && (
        <ScrollableUncontrolledPopover
          placement="left"
          trigger="hover"
          target={uniquePopoverId}
          className="popover-variable-lg"
        >
          <div>
            {calibrationInfo && (
              <CalibrationPopover
                question={question}
                calibrationInfo={calibrationInfo}
              />
            )}
            {!!person.manager && (
              <div
                className={
                  calibrationInfo?.calibrationItems?.length ? 'mt-3' : ''
                }
              >
                <span className="fw-bold">
                  {person.manager.full_name}
                  <FormattedMessage
                    id="app.views.widgets.dashboards.team_dashboard.colon"
                    defaultMessage=":"
                  />
                </span>{' '}
                <FormattedMessage
                  id="app.views.widgets.dashboards.team_dashboard.additional_managers.promoted_recommendation"
                  defaultMessage="Recommended: {value, select, true {Yes} other {No}}"
                  values={{
                    value: person.extracted_recommend_for_promotion,
                  }}
                />
                {!!person.promotion_comments && (
                  <div className="mt-2">
                    <FormattedMessage
                      id="app.views.widgets.dashboards.team_dashboard.additional_managers.promoted_recommendation_comments"
                      defaultMessage="Comments: {value}"
                      values={{
                        value: person.promotion_comments,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {person.recommend_for_promotion_additional_managers.length > 0 && (
            <div>
              {person.recommend_for_promotion_additional_managers.map(
                ({ manager, recommend_for_promotion, promotion_comments }) => (
                  <div className="mt-3" key={manager.id}>
                    <span className="fw-bold">
                      {manager.full_name}
                      <FormattedMessage
                        id="app.views.widgets.dashboards.team_dashboard.colon"
                        defaultMessage=":"
                      />
                    </span>{' '}
                    <FormattedMessage
                      id="app.views.widgets.dashboards.team_dashboard.additional_managers.promoted_recommendation"
                      defaultMessage="Recommended: {value, select, true {Yes} other {No}}"
                      values={{
                        value: recommend_for_promotion,
                      }}
                    />
                    {!!promotion_comments && (
                      <div className="mt-2">
                        {' '}
                        <FormattedMessage
                          id="app.views.widgets.dashboards.team_dashboard.additional_managers.promoted_recommendation_comments"
                          defaultMessage="Comments: {value}"
                          values={{
                            value: promotion_comments,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </ScrollableUncontrolledPopover>
      )}
    </>
  );
};

export default TeamDashboardManagerPromotion;
